import React, { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useDeactivateBusiness,
  useDeactivateBusinessPreview,
} from '~/hooks/apis/business-traveller/useDeactivateBusiness';
import useToggleState from '~/hooks/useToggleState';

import { getBusiness } from '~/services/BusinessTraveller/BusinessTravellerService';

const STEP = {
  PREVIEW: 1,
  CONFIRM: 2,
};

interface Props {
  businessId: string;
}

export default function BusinessDeactivateButton({ businessId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { isToggled: isModalOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState();

  const [step, setStep] = useState(1);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');

  const { data: business } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => getBusiness(businessId),
    enabled: !!businessId,
  });

  const isDeactivated = !!business?.deletedAt;

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    error: previewError,
  } = useDeactivateBusinessPreview(businessId, isModalOpen);

  const affectedRecords = previewData?.result?.affectedRecords;
  const businessName = previewData?.result?.business?.name ?? business?.name ?? '';

  const deactivateMutation = useDeactivateBusiness();

  const nameMatches = confirmName.trim().toLowerCase() === businessName.trim().toLowerCase();
  const canConfirm = acknowledged && nameMatches && reason.trim().length > 0;

  function handleOpenModal() {
    setStep(STEP.PREVIEW);
    setAcknowledged(false);
    setConfirmName('');
    setReason('');
    openModal();
  }

  const handleConfirm = useCallback(() => {
    deactivateMutation.mutate(
      { businessId, reason: reason.trim() },
      {
        onSuccess: () => {
          enqueueSnackbar('Business deactivated successfully', { variant: 'success' });
          closeModal();
        },
        onError: (error: Error) => {
          enqueueSnackbar(`Error deactivating business: ${error.message}`, { variant: 'error' });
        },
      },
    );
  }, [businessId, reason, closeModal, deactivateMutation, enqueueSnackbar]);

  return (
    <>
      <Tooltip title={isDeactivated ? 'This business has already been deactivated' : ''}>
        <span>
          <Button variant="outlined" color="error" onClick={handleOpenModal} disabled={isDeactivated}>
            Deactivate Business
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={isModalOpen}
        onClose={deactivateMutation.isPending ? undefined : closeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deactivate Business</DialogTitle>
        <DialogContent>
          {step === STEP.PREVIEW && (
            <Stack spacing={2}>
              <Alert severity="warning">This action cannot be undone.</Alert>
              <Typography>
                Deactivating <strong>{businessName}</strong> will permanently deactivate the business and all associated
                records. No accounts will be able to sign in to this business after deactivation.
              </Typography>

              {isPreviewLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {previewError && (
                <Alert severity="error">
                  {previewError instanceof Error
                    ? previewError.message
                    : 'Failed to load affected records. Please try again.'}
                </Alert>
              )}

              {affectedRecords && (
                <Alert severity="info">
                  <Typography variant="subtitle2" gutterBottom>
                    The following records will be deactivated:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {affectedRecords.employees > 0 && <li>{affectedRecords.employees} employees</li>}
                    {affectedRecords.policies > 0 && <li>{affectedRecords.policies} policies</li>}
                    {affectedRecords.policyBudgets > 0 && <li>{affectedRecords.policyBudgets} policy budgets</li>}
                    {affectedRecords.approvalPolicies > 0 && (
                      <li>{affectedRecords.approvalPolicies} approval policies</li>
                    )}
                    {affectedRecords.frequentFlyers > 0 && (
                      <li>{affectedRecords.frequentFlyers} frequent flyer records</li>
                    )}
                  </ul>
                </Alert>
              )}
            </Stack>
          )}

          {step === STEP.CONFIRM && (
            <Stack spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                label="I understand this will permanently deactivate this business and all associated records"
              />

              <TextField
                label={`Type "${businessName}" to confirm`}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                fullWidth
                error={confirmName.length > 0 && !nameMatches}
                helperText={confirmName.length > 0 && !nameMatches ? 'Business name does not match' : ''}
              />

              <TextField
                label="Reason for deactivation"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                required
                multiline
                rows={3}
                helperText="Required. Please provide a reason for deactivating this business."
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button disabled={deactivateMutation.isPending} variant="text" onClick={closeModal}>
            Cancel
          </Button>

          {step === STEP.PREVIEW && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep(STEP.CONFIRM)}
              disabled={isPreviewLoading || !!previewError}
            >
              Next
            </Button>
          )}

          {step === STEP.CONFIRM && (
            <>
              <Button variant="text" onClick={() => setStep(STEP.PREVIEW)}>
                Back
              </Button>
              <LoadingButton
                loading={deactivateMutation.isPending}
                variant="contained"
                color="error"
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                Confirm Deactivation
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
